import { inject, Injectable } from '@angular/core';
import { HttpWithCacheService } from '../http-with-cache.service';
import { CedricApiService } from '../../infrastructure/api/cedric.api.service';
import { map, Observable } from 'rxjs';
import {
  Deliverable,
  DeliverablesViewModel,
  EditableDeliverable,
  EntityItem,
  FundEntityItem,
  IServerResponse,
} from '@aksia/models';
import { ENVIRONMENT_TOKEN } from '@aksia/infrastructure';

@Injectable({
  providedIn: 'root',
})
export class DeliverablesService extends HttpWithCacheService {
  private api = inject(CedricApiService);
  protected environment = inject(ENVIRONMENT_TOKEN);

  private controller: string = 'ProjectDeliverables';

  constructor() {
    super();
  }

  getDeliverablesViewModel(
    forceHttp = false,
  ): Observable<DeliverablesViewModel> {
    return this.useCache(
      this.api.post(`${this.controller}/GetProjectDeliverablesViewModel`, {}),
      { forceHttp },
    );
  }

  getDeliverables(request: {
    entityId: number;
    entityTypeId: string;
    isComplete: boolean;
    isCancelled: boolean;
  }): Observable<Deliverable[]> {
    return this.api.post(`${this.controller}/GetProjectDeliverables`, request);
  }

  getExistingProject(
    projectTypeId: number,
    fundId: number,
    clientId: number,
  ): Observable<{
    existingProject: any;
    recurrence?: number;
    plainAnalystId?: number;
    relatedProjects?: any[];
  }> {
    return this.api.post(`${this.controller}/GetExistingProject`, {
      projectTypeId,
      fundId,
      clientId,
    });
  }

  exportToExcel(
    entityId: number,
    entityTypeId: string,
    isComplete: boolean,
    isCancelled: boolean,
  ) {
    window.open(
      `${this.environment.cedric_api_url}/${this.controller}/Export?entityId=${entityId}&entityTypeId=${entityTypeId}&isComplete=${isComplete}&isCancelled=${isCancelled}`,
      '_blank',
    );
  }

  exportMultiClientToExcel(
    clientIds: number[],
    isComplete: boolean,
    isCancelled: boolean,
  ) {
    window.open(
      `${this.environment.cedric_api_url}/${this.controller}/ExportMultiClient?clientIds=${clientIds.join(',')}&isComplete=${isComplete}&isCancelled=${isCancelled}`,
      '_blank',
    );
  }

  removeMonitoring(
    clientId: number,
    fundId: number,
  ): Observable<IServerResponse> {
    return this.api.post(`${this.controller}/DeleteClientFundMonitor`, {
      clientId,
      fundId,
    });
  }

  searchEntities(
    input: string,
    entityTypeId?: string,
  ): Observable<EntityItem[]> {
    return this.api.post(`${this.controller}/SearchEntities`, {
      query: input,
      entityTypeId,
    });
  }

  createDeliverable(
    deliverable: EditableDeliverable,
  ): Observable<IServerResponse> {
    return this.api.post(
      `${this.controller}/CreateProjectDeliverable`,
      deliverable,
    );
  }

  updateDeliverable(deliverable: any): Observable<IServerResponse> {
    return this.api.post(
      `${this.controller}/UpdateProjectDeliverable`,
      deliverable,
    );
  }

  searchFundsBasicData(fundId: number): Observable<FundEntityItem> {
    return this.api
      .post(`${this.controller}/SearchFundsBasicData`, { fundId })
      .pipe(map((res) => res?.[0]));
  }
}
